import { defineStore } from 'pinia'
import { APIS, LOADERS } from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import type { ICenter } from 'src/interfaces/center'
import type { IOrgInfo, IOrgInfoWithToken } from 'src/interfaces/org'
import { fail } from 'src/utils/notify'
import { useAuthStore } from 'src/common/store'

type SelectedOrgInfo = IOrgInfo & { sasToken?: string }

export interface IOrgState {
  orgAlias: string
  orgName: string
  centers: ICenter[]
  orgInfoList: IOrgInfoWithToken[]
  orgInfoListLoaded: boolean
  orgInfoListLoading: boolean
  selectedOrg?: SelectedOrgInfo
  publicRegistrationOrg?: IOrgInfo
}

export const useOrgStore = defineStore('pinia/org', {
  state: () =>
    ({
      orgAlias: '',
      orgName: '',
      centers: [],
      orgInfoList: [],
      orgInfoListLoaded: false,
      orgInfoListLoading: false,
      selectedOrg: undefined,
      publicRegistrationOrg: undefined,
    } as IOrgState),
  getters: {
    currentCenter(state): ICenter | undefined {
      const authStore = useAuthStore()
      return state.centers.find((c) => c.id === authStore.currentCenterId)
    },
  },
  actions: {
    async init() {
      const authStore = useAuthStore()
      const needsInitialSuperAdminOrg =
        authStore.isSuperAdmin &&
        !authStore.lastSessionInfo.lastOrgId &&
        !authStore.currentOrgId

      if (needsInitialSuperAdminOrg) {
        await this.getOrgInfoList()
        const firstOrg = this.orgInfoList[0]
        if (firstOrg) {
          await this.selectOrg(firstOrg)
        }
        return
      }

      const {
        useFetchLoader: fetchOrgInfo,
        result: orgInfo,
        error: fetchOrgInfoError,
      } = useLoader<IOrgInfo>(LOADERS.res, { uri: APIS.orgInfo })

      await fetchOrgInfo()
      if (fetchOrgInfoError.value) {
        fail(fetchOrgInfoError.value.message)
        return
      }

      this.selectedOrg = orgInfo.value
      if (!this.selectedOrg) {
        this.centers = []
        return
      }

      await this.getOrgCenters()
    },
    async getOrgCenters() {
      const {
        useFetchLoader: fetchCenterList,
        result: centers,
        error: fetchCenterListError,
      } = useLoader<ICenter[]>(LOADERS.res, { uri: APIS.centers })

      await fetchCenterList()
      if (fetchCenterListError.value) {
        this.centers = []
        fail(fetchCenterListError.value.message)
        return
      }

      this.centers = centers.value ?? []
    },
    async getOrgInfoList() {
      const authStore = useAuthStore()
      if (
        !authStore.isSuperAdmin ||
        this.orgInfoListLoaded ||
        this.orgInfoListLoading
      )
        return

      this.orgInfoListLoading = true
      const {
        useFetchLoader: fetchOrgInfoList,
        result: orgInfoList,
        error: fetchOrgInfoListError,
      } = useLoader<IOrgInfoWithToken[]>(LOADERS.res, { uri: APIS.orgInfoList })
      await fetchOrgInfoList()
      if (fetchOrgInfoListError.value) {
        this.orgInfoListLoading = false
        fail(fetchOrgInfoListError.value.message)
        return
      }
      this.orgInfoList = orgInfoList.value ?? []
      this.orgInfoListLoaded = true
      this.orgInfoListLoading = false

      if (!orgInfoList.value || orgInfoList.value.length === 0) {
        return
      }

      const orgInfo = orgInfoList.value?.find(
        (oInfo) => oInfo.id === authStore.currentOrgId
      )

      if (orgInfo) {
        this.selectedOrg = orgInfo
      }
    },
    async selectOrg(org: IOrgInfoWithToken) {
      this.setSelectedOrg(org)
      await this.getOrgCenters()
    },
    setSelectedOrg(org?: SelectedOrgInfo) {
      this.selectedOrg = org
      this.centers = []
      const authStore = useAuthStore()
      authStore.setCurrentOrgId(org?.id)
    },
    setOrgName(orgName: string) {
      this.orgName = orgName
    },
    setOrgCenters(centers: ICenter[]) {
      this.centers = centers
    },
    setPublicRegistrationOrg(org: IOrgInfo) {
      this.publicRegistrationOrg = org
    },
  },
})
